import React, { useState, useEffect, useMemo, Fragment } from 'react';
import { Link as RouterLink, useOutletContext } from 'react-router-dom';
import {
    Container, Box, Paper, Typography, Alert,
    Stack, Link, Divider, Skeleton, TableContainer,
    Table, TableHead, TableRow, TableCell, TableBody, Collapse, TableSortLabel, Button,
    Grid, useTheme, useMediaQuery, Chip, Tooltip
} from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SportsHockeyIcon from '@mui/icons-material/SportsHockey';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import CakeIcon from '@mui/icons-material/Cake';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import ContactsIcon from '@mui/icons-material/Contacts';
import LinkIcon from '@mui/icons-material/Link';
import { visuallyHidden } from '@mui/utils';
import { logEvent } from '../analytics';
import {
    EventType,
    formatLocation,
    getEventType,
    getLiveBarnLink,
    formatEventTime,
    isMultiDayEvent,
    isAllDayEvent,
    formatEventDateRange,
    getNextEvents,
    convertTournamentGameToCalendarEvent,
    mergeTournamentAndCalendarEvents,
    getCleanVolunteerDuties,
    getGameTeams
} from '../utils/eventUtils';
import { Player, Team, Game, ScoredGame, CalendarEvent } from '../types';
import { fetchAndProcessData } from '../utils/hockeyStatsParser';
import { User } from "firebase/auth";
import ScheduleConflictCard from './ScheduleConflictCard';
import { findScheduleConflicts, ScheduleConflict } from '../utils/scheduleConflictUtils';
import { fetchAndMergeCalendarData } from '../utils/dataUtils';
import WeeklyMatchups from './WeeklyMatchups';
import { useSeason } from '../SeasonContext';
import { useRoster } from '../RosterContext';
import { CURRENT_SEASON, CURRENT_SEASON_KEY } from '../seasons';
import { getActiveOrUpcomingTournament, parseLocalDate, TOURNAMENTS, TournamentInfo } from '../tournaments';
import { fetchTournamentDetails, getCachedTournamentData, TournamentDetails } from '../utils/tournamentScraper';
import TournamentCard from './TournamentCard';
import NextDutyCard from './NextDutyCard';
import {
    getCachedHomeLayout,
    setCachedHomeLayout,
    getDefaultHomeLayout,
    HomeCardId,
    CachedHomeLayout
} from '../utils/homeLayoutCache';

// --- Helper functions for sorting the schedule table ---
function stableSort<T>(array: readonly T[], comparator: (a: T, b: T) => number) {
    const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) return order;
        return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
}

type Order = 'asc' | 'desc';

function getComparator(order: Order, orderBy: keyof Game): (a: Game, b: Game) => number {
    return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

function descendingComparator(a: Game, b: Game, orderBy: keyof Game) {
    const dateA = new Date(a[orderBy] as string);
    const dateB = new Date(b[orderBy] as string);
    if (dateB < dateA) return -1;
    if (dateB > dateA) return 1;
    return 0;
}

const dayDiff = (date1: Date, date2: Date): number => {
    const _MS_PER_DAY = 1000 * 60 * 60 * 24;
    const utc1 = Date.UTC(date1.getFullYear(), date1.getMonth(), date1.getDate());
    const utc2 = Date.UTC(date2.getFullYear(), date2.getMonth(), date2.getDate());
    return Math.floor((utc2 - utc1) / _MS_PER_DAY);
};

const formatRelativeTime = (eventDate: Date): string => {
    const today = new Date();
    const diff = dayDiff(new Date(today.toDateString()), new Date(eventDate.toDateString()));
    if (diff === 0) return "Today";
    if (diff === 1) return "Tomorrow";
    return `in ${diff} days`;
};


const SingleEventContent: React.FC<{ event: CalendarEvent }> = ({ event }) => {
    const eventDate = new Date(event.start);
    const relativeTime = formatRelativeTime(eventDate);    
    const startTime = formatEventTime(event, 'start');
    const endTime = event.end ? formatEventTime(event, 'end') : '';
    const formattedTime = (endTime && endTime !== startTime) ? `${startTime} - ${endTime}` : startTime;
    const eventType = getEventType(event);
    const isGame = eventType === EventType.Game || eventType === EventType.Exhibition || eventType === EventType.Tournament;
    const isMultiDay = isMultiDayEvent(event);
    const isAllDay = isMultiDay || isAllDayEvent(event);
    const isInternalLink = event.url?.startsWith('/');

    const gameTeams = getGameTeams(event);
    const cleanDuties = getCleanVolunteerDuties(event.duties);
    const isHomeJets = Boolean(gameTeams && /\b(?:jets|csdhl)\b/i.test(gameTeams.homeTeam));
    const isAwayJets = Boolean(gameTeams && /\b(?:jets|csdhl)\b/i.test(gameTeams.awayTeam));

    const today = new Date();
    const diff = dayDiff(new Date(today.toDateString()), new Date(eventDate.toDateString()));
    const dateFormatted = !isMultiDay && diff > 1
        ? `${eventDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'America/Chicago' })} (${relativeTime})`
        : relativeTime;

    return (
        <Box>
            {event.tournamentName && (
                <Chip
                    label={event.tournamentName}
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ mb: 0.75, fontWeight: 600, fontSize: '0.72rem', height: 22 }}
                />
            )}
            <Typography variant="body1">
                {event.url ? (
                    isInternalLink ? (
                        <Link component={RouterLink} to={event.url} onClick={() => logEvent('click_event_link', { event_summary: event.summary, url: event.url })}>
                            {event.summary}
                        </Link>
                    ) : (
                        <Link href={event.url} target="_blank" rel="noopener noreferrer" onClick={() => logEvent('click_event_link', { event_summary: event.summary, url: event.url })}>
                            {event.summary}
                        </Link>
                    )
                ) : event.summary}
            </Typography>

            {gameTeams && (
                <Paper variant="outlined" sx={{ p: { xs: 1, sm: 1.25 }, my: 1.25, bgcolor: 'grey.50', borderRadius: 1.5 }}>
                    {/* HOME Team */}
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
                        <Chip
                            label="HOME"
                            size="small"
                            sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, bgcolor: 'grey.200', color: 'text.secondary', flexShrink: 0 }}
                        />
                        <Typography
                            variant="body2"
                            sx={{
                                fontWeight: isHomeJets ? 700 : 500,
                                color: isHomeJets ? 'primary.main' : 'text.primary',
                                fontSize: { xs: '0.875rem', sm: '0.95rem' }
                            }}
                        >
                            {gameTeams.homeTeam}
                        </Typography>
                    </Stack>
                    {/* AWAY Team */}
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Chip
                            label="AWAY"
                            size="small"
                            sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, bgcolor: 'grey.200', color: 'text.secondary', flexShrink: 0 }}
                        />
                        <Typography
                            variant="body2"
                            sx={{
                                fontWeight: isAwayJets ? 700 : 500,
                                color: isAwayJets ? 'primary.main' : 'text.primary',
                                fontSize: { xs: '0.875rem', sm: '0.95rem' }
                            }}
                        >
                            {gameTeams.awayTeam}
                        </Typography>
                    </Stack>
                </Paper>
            )}

            <Typography variant="body2" color="text.secondary">
                {isMultiDay ? `${formatEventDateRange(event)} (${relativeTime})` : `${dateFormatted}${!isAllDay ? ` at ${formattedTime}` : ''}`}
            </Typography>
            {event.location && (
                <Typography variant="body2" color="text.secondary">{formatLocation(event.location, event)}</Typography>
            )}
            {isGame && !isAllDay && !isMultiDay && (
                <Box sx={{ mt: 1 }}>{getLiveBarnLink(event.location, event.start)}</Box>
            )}
            {event.isRollover && (
                <Box sx={{ mt: 1.5, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <InfoOutlinedIcon fontSize="small" color="action" />
                        <Typography variant="caption" color="text.secondary">
                            This is a rollover practice. The original event title is: "
                            <Typography variant="caption" component="span" sx={{ fontStyle: 'italic' }}>{event.originalSummary}</Typography>
                            ". The full practice time is {new Date(event.originalStart!).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'America/Chicago' })}
                            {' - '}
                            {new Date(event.originalEnd!).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'America/Chicago' })}.
                        </Typography>
                    </Stack>
                </Box>
            )}
            {cleanDuties && Object.keys(cleanDuties).length > 0 && (
                <Box sx={{ mt: 1.5, pt: 1.5, borderTop: 1, borderColor: 'divider' }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>Volunteers:</Typography>
                    <Stack component="ul" sx={{ m: 0, p: 0, pl: 2 }}>
                        {Object.entries(cleanDuties).map(([duty, name]) => (
                            <Typography component="li" key={duty} variant="body2" color="text.secondary" sx={{ listStyleType: 'none' }}>
                                <strong>{duty}:</strong> {name}
                            </Typography>
                        ))}
                    </Stack>
                </Box>
            )}
        </Box>
    );
};

// --- Standings Components ---
const formatRank = (rank: number): string => {
    if (rank % 100 >= 11 && rank % 100 <= 13) {
        return `${rank}th`;
    }
    switch (rank % 10) {
        case 1: return `${rank}st`;
        case 2: return `${rank}nd`;
        case 3: return `${rank}rd`;
        default: return `${rank}th`;
    }
};

const ScheduleAndStats: React.FC<{ team: Team; allGames: ScoredGame[]; fullSchedule: Game[] }> = ({ team, allGames, fullSchedule }) => {
    const [order, setOrder] = useState<Order>('asc');
    const [orderBy, setOrderBy] = useState<keyof Game>('scheduleStartTime');

    const handleRequestSort = (property: keyof Game) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const scoresMap = new Map<string, { finalScore: Game['finalScore'], isPendingFinal?: boolean }>();
    allGames.forEach(g => {
        const id = g.game?.id || g.game?.gameId;
        if (id && g.game.finalScore) {
            scoresMap.set(id, {
                finalScore: g.game.finalScore,
                isPendingFinal: g.game.isPendingFinal
            });
        }
    });

    const teamSchedule = fullSchedule
        .filter(game => game.homeTeam.id === team.id || game.visitorTeam.id === team.id)
        .map(game => {
            const details = scoresMap.get(game.id) || scoresMap.get(game.gameId || '');
            return {
                ...game,
                finalScore: details?.finalScore || game.finalScore,
                isPendingFinal: details?.isPendingFinal ?? game.isPendingFinal
            };
        });

    const getGameResult = (game: Game & { isPendingFinal?: boolean }) => {
        if (game.finalScore) {
            const { homeGoals, visitorGoals } = game.finalScore;
            const isHomeTeam = team.name === game.homeTeam.name;
            let outcome = 'T';
            if (isHomeTeam) {
                if (homeGoals > visitorGoals) outcome = 'W'; else if (homeGoals < visitorGoals) outcome = 'L';
            } else {
                if (visitorGoals > homeGoals) outcome = 'W'; else if (visitorGoals < homeGoals) outcome = 'L';
            }
            return {
                result: outcome,
                score: `${visitorGoals} - ${homeGoals}`,
                isPendingFinal: Boolean(game.isPendingFinal)
            };
        }
        return { result: 'TBD', score: '', isPendingFinal: false };
    };

    return (
        <Box sx={{ margin: 1, padding: 2 }}>
            <Typography variant="h6" gutterBottom component="div">Statistics</Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 4 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>GP</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>GF</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>GA</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>+/-</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow>
                            <TableCell>{team.wins + team.losses + team.ties}</TableCell>
                            <TableCell>{team.goalsScored}</TableCell>
                            <TableCell>{team.goalsScored - team.totalGoalDifferential}</TableCell>
                            <TableCell>{team.totalGoalDifferential}</TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>
            <Typography variant="h6" gutterBottom component="div">Schedule</Typography>
            <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>Result</TableCell>
                            <TableCell sortDirection={orderBy === 'scheduleStartTime' ? order : false} sx={{ fontWeight: 'bold' }}>
                                <TableSortLabel active={orderBy === 'scheduleStartTime'} direction={orderBy === 'scheduleStartTime' ? order : 'asc'} onClick={() => handleRequestSort('scheduleStartTime')}>
                                    Date
                                    <Box component="span" sx={visuallyHidden}>{order === 'desc' ? 'sorted descending' : 'sorted ascending'}</Box>
                                </TableSortLabel>
                            </TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Visitor</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Home</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Location</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {stableSort(teamSchedule, getComparator(order, orderBy)).map(game => {
                            const { result, score, isPendingFinal } = getGameResult(game);
                            return (
                                <TableRow key={game.id}>
                                    <TableCell>
                                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                            <strong>{result}</strong> {score}
                                            {isPendingFinal && (
                                                <Tooltip title="Unofficial Final (Pending official review)" arrow>
                                                    <AccessTimeIcon sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} aria-label="Unofficial Final - Pending official review" />
                                                </Tooltip>
                                            )}
                                        </Box>
                                    </TableCell>
                                    <TableCell>{game.date} {game.time}</TableCell>
                                    <TableCell>{game.visitorTeam.name}</TableCell>
                                    <TableCell>{game.homeTeam.name}</TableCell>
                                    <TableCell>{formatLocation(game.location)}</TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

const StandingsRow: React.FC<{ team: Team; isExpanded: boolean; onRowClick: () => void; allGames: ScoredGame[]; fullSchedule: Game[] }> = ({ team, isExpanded, onRowClick, allGames, fullSchedule }) => {
    return (
        <Fragment>
            <TableRow hover onClick={onRowClick} sx={{ cursor: 'pointer', '& > *': { borderBottom: 'unset' } }}>
                <TableCell component="th" scope="row">{formatRank(team.rank)}</TableCell>
                <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {team.logoUrl && (
                            <Box sx={{ width: 56, height: 40, mr: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                               <img src={team.logoUrl} alt={`${team.name} logo`} style={{ maxHeight: '100%', maxWidth: '100%', width: 'auto', height: 'auto' }} onError={(e) => { (e.currentTarget as HTMLElement).style.display = 'none'; }} />
                            </Box>
                        )}
                        {team.name}
                    </Box>
                </TableCell>
                <TableCell align="right">{team.wins}</TableCell>
                <TableCell align="right">{team.losses}</TableCell>
                <TableCell align="right">{team.ties}</TableCell>
                <TableCell align="right" sx={{fontWeight: 'bold'}}>{team.points}</TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        {isExpanded && <ScheduleAndStats team={team} allGames={allGames} fullSchedule={fullSchedule} />}
                    </Collapse>
                </TableCell>
            </TableRow>
        </Fragment>
    );
};


interface OutletContextType {
    user: User | null;
    isTeamManager: boolean;
}

const HomePage = () => {
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
    const { selectedSeason, isViewingPastSeason } = useSeason();
    const { getRoster, isAuthReady } = useRoster();
    const [allEvents, setAllEvents] = useState<CalendarEvent[]>([]);
    const [jetsTeam, setJetsTeam] = useState<Team | null>(null);
    const [allGames, setAllGames] = useState<ScoredGame[]>([]);
    const [fullSchedule, setFullSchedule] = useState<Game[]>([]);
    const [standings, setStandings] = useState<Team[]>([]);
    const [isStandingsExpanded, setIsStandingsExpanded] = useState(false);
    const [calendarLoading, setCalendarLoading] = useState(true);
    const [statsLoading, setStatsLoading] = useState(true);
    const { user, isTeamManager } = useOutletContext<OutletContextType>();
    const [calendarError, setCalendarError] = useState<string | null>(null);
    const [statsError, setStatsError] = useState<string | null>(null);

    const [cachedLayout] = useState<CachedHomeLayout>(() => (
        getCachedHomeLayout(selectedSeason.key) || getDefaultHomeLayout(selectedSeason.key)
    ));

    const isLoading = calendarLoading || statsLoading;

    const userEmail = user?.email?.toLowerCase();
    const currentSeasonRoster = getRoster(CURRENT_SEASON_KEY) || getRoster('pw_26_27');

    // --- Active or Upcoming Tournament Relevance Calculation ---
    const activeOrUpcomingTournament = useMemo(() => {
        return getActiveOrUpcomingTournament(selectedSeason.key || CURRENT_SEASON_KEY);
    }, [selectedSeason.key]);

    // Also look up tournament for any calendar tournament event (even if >14 days ahead)
    const calendarTournament = useMemo<TournamentInfo | null>(() => {
        if (!allEvents || allEvents.length === 0) return null;
        const tEvent = allEvents.find(e => e.isTournament || getEventType(e) === EventType.Tournament);
        if (!tEvent) return null;
        return TOURNAMENTS.find(t =>
            (tEvent.url && tEvent.url.includes(t.key)) ||
            (tEvent.summary && tEvent.summary.toLowerCase().includes(t.name.toLowerCase())) ||
            (t.name.toLowerCase().includes((tEvent.summary || '').toLowerCase())) ||
            (t.startDate && tEvent.start && tEvent.start.startsWith(t.startDate))
        ) || null;
    }, [allEvents]);

    const targetTournament = activeOrUpcomingTournament?.tournament || calendarTournament;

    // Fast synchronous cache read on mount
    const [tournamentDetails, setTournamentDetails] = useState<TournamentDetails | null>(() => {
        const tKey = activeOrUpcomingTournament?.tournament?.key || calendarTournament?.key;
        if (!tKey) return null;
        const cached = getCachedTournamentData(tKey);
        if (cached) {
            return {
                games: cached.games || [],
                standings: cached.pools || [],
                isScheduleAvailable: (cached.games || []).length > 0,
                updatedAt: cached.updatedAt,
                isLive: cached.isLive,
                stale: cached.stale,
                cached: cached.cached,
            };
        }
        return null;
    });

    // Fetch tournament details in background when targetTournament is detected and auth is ready
    useEffect(() => {
        if (!targetTournament || !isAuthReady) return;

        let isMounted = true;
        fetchTournamentDetails(targetTournament)
            .then(data => {
                if (isMounted && data) {
                    setTournamentDetails(data);
                }
            })
            .catch(err => {
                console.warn('Error fetching tournament details in HomePage:', err);
            });

        return () => {
            isMounted = false;
        };
    }, [targetTournament, isAuthReady]);

    // Structured Jets tournament games converted to CalendarEvents
    const jetsTournamentGames = useMemo<CalendarEvent[]>(() => {
        if (!targetTournament || !tournamentDetails?.games || tournamentDetails.games.length === 0) {
            return [];
        }
        return tournamentDetails.games
            .filter(g =>
                (g.homeTeam || '').toLowerCase().includes('jets') ||
                (g.visitorTeam || '').toLowerCase().includes('jets')
            )
            .map(g => convertTournamentGameToCalendarEvent(g, targetTournament));
    }, [targetTournament, tournamentDetails]);

    // Derive upcomingEvents, nextEvents, nextGame
    const { upcomingEvents, nextEvents, nextGame } = useMemo(() => {
        if (!allEvents || allEvents.length === 0) {
            return { upcomingEvents: [], nextEvents: [], nextGame: null };
        }

        // An event is "upcoming" if it hasn't started more than 30 minutes ago.
        const cutoffTime = new Date();
        cutoffTime.setMinutes(cutoffTime.getMinutes() - 30);

        // Deduplicate and merge tournament games with team calendar events
        const eventsToProcess = mergeTournamentAndCalendarEvents(allEvents, jetsTournamentGames, targetTournament);

        const upcoming = eventsToProcess.filter(event => new Date(event.start) > cutoffTime);

        const filteredEvents = upcoming.filter(event => {
            const type = getEventType(event);
            return type !== EventType.Goalie && type !== EventType.Holiday && type !== EventType.Birthday;
        });

        // Find next game from calendar events
        const firstCalendarGame = filteredEvents.find(e => {
            const type = getEventType(e);
            return type === EventType.Game || type === EventType.Exhibition || type === EventType.Tournament;
        });

        const computedNextEvents = getNextEvents(filteredEvents);

        let resolvedNextGame: CalendarEvent | null = null;

        if (computedNextEvents.length > 0) {
            const hasGameInNextEvents = computedNextEvents.some(event => {
                const type = getEventType(event);
                return type === EventType.Game || type === EventType.Exhibition || type === EventType.Tournament ||
                    (firstCalendarGame && event.summary === firstCalendarGame.summary && event.start === firstCalendarGame.start);
            });

            if (!hasGameInNextEvents) {
                resolvedNextGame = firstCalendarGame || null;
            }
        } else {
            resolvedNextGame = firstCalendarGame || null;
        }

        return {
            upcomingEvents: upcoming,
            nextEvents: computedNextEvents,
            nextGame: resolvedNextGame
        };
    }, [allEvents, targetTournament, jetsTournamentGames]);

    const isTournamentBeforeNextEvent = useMemo(() => {
        if (!activeOrUpcomingTournament) return false;
        if (activeOrUpcomingTournament.isOngoing) return true;
        if (!nextEvents || nextEvents.length === 0) return false;

        const nextEventStart = new Date(nextEvents[0].start);
        const tournamentStart = parseLocalDate(activeOrUpcomingTournament.tournament.startDate);

        return tournamentStart <= nextEventStart;
    }, [activeOrUpcomingTournament, nextEvents]);

    const tournamentCardElement = activeOrUpcomingTournament ? (
        <TournamentCard
            tournament={activeOrUpcomingTournament.tournament}
            calendarEvents={allEvents}
            tournamentDetails={tournamentDetails}
        />
    ) : null;

    // --- Next Duty Calculation (derived reactively) ---
    const nextDuty = useMemo(() => {
        if (!userEmail || !currentSeasonRoster) return null;
        let userLastName: string | null = null;
        for (const player of currentSeasonRoster.players) {
            const parentEmails = player.parents.map(p => p.email?.toLowerCase()).filter(Boolean);
            if (parentEmails.includes(userEmail)) {
                userLastName = player.lastName;
                break;
            }
        }
        if (!userLastName) return null;

        for (const event of upcomingEvents) {
            if (event.duties) {
                const dutyFound = Object.entries(event.duties).find(([, name]) => typeof name === 'string' && name.includes(userLastName!));
                if (dutyFound) {
                    return { event, duty: dutyFound[0] };
                }
            }
        }
        return null;
    }, [userEmail, currentSeasonRoster, upcomingEvents]);

    const daysUntilNextDuty = nextDuty ? dayDiff(new Date(), new Date(nextDuty.event.start)) : null;

    // --- Schedule Conflicts Calculation (for team managers) ---
    const scheduleConflicts = useMemo<ScheduleConflict[]>(() => {
        if (!isTeamManager || allEvents.length === 0 || fullSchedule.length === 0) {
            return [];
        }
        const leagueTeamNames = new Set(standings.map(team => team.name));
        return findScheduleConflicts(allEvents, fullSchedule, leagueTeamNames);
    }, [isTeamManager, allEvents, fullSchedule, standings]);

    const ScheduleConflictCardElement = isTeamManager && scheduleConflicts.length > 0 ? (
        <ScheduleConflictCard conflicts={scheduleConflicts} />
    ) : null;

    // --- Birthday Calculation (derived reactively) ---
    const { nextBirthdayPlayer, daysUntilBirthday, isBirthdayToday } = useMemo(() => {
        if (!currentSeasonRoster || !currentSeasonRoster.players || currentSeasonRoster.players.length === 0) {
            return { nextBirthdayPlayer: null, daysUntilBirthday: null, isBirthdayToday: false };
        }
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Normalize to start of day for comparison

        const players = currentSeasonRoster.players;
        let upcomingBirthday: { player: Player; date: Date; diff: number } | null = null;

        for (const player of players) {
            const dob = new Date(player.dob);
            const birthMonth = dob.getMonth();
            const birthDate = dob.getDate();

            // Check for today's birthday first
            if (birthMonth === today.getMonth() && birthDate === today.getDate()) {
                return { nextBirthdayPlayer: player, daysUntilBirthday: 0, isBirthdayToday: true };
            }

            let nextBirthdayThisYear = new Date(today.getFullYear(), birthMonth, birthDate);
            nextBirthdayThisYear.setHours(0, 0, 0, 0);

            if (nextBirthdayThisYear < today) {
                nextBirthdayThisYear.setFullYear(today.getFullYear() + 1);
            }

            const diff = dayDiff(today, nextBirthdayThisYear);

            if (!upcomingBirthday || diff < upcomingBirthday.diff) {
                upcomingBirthday = { player, date: nextBirthdayThisYear, diff };
            }
        }

        if (upcomingBirthday) {
            return { nextBirthdayPlayer: upcomingBirthday.player, daysUntilBirthday: upcomingBirthday.diff, isBirthdayToday: false };
        }

        return { nextBirthdayPlayer: null, daysUntilBirthday: null, isBirthdayToday: false };
    }, [currentSeasonRoster]);

    const NextDutyCardElement = nextDuty ? (
        <NextDutyCard
            duty={nextDuty.duty}
            event={nextDuty.event}
            daysUntil={daysUntilNextDuty}
        />
    ) : null;

    useEffect(() => {
        if (isViewingPastSeason) {
            document.title = `Jets '14 | ${selectedSeason.label}`;
            return;
        }

        document.title = "Jets '14 | Home";
        let isMounted = true;

        const fetchCalendarData = async () => {
            try {
                const mergedEvents = await fetchAndMergeCalendarData();
                if (!isMounted) return;

                setAllEvents(mergedEvents);
            } catch (e: any) {
                if (isMounted) {
                    setCalendarError(e.message);
                    console.error("Failed to fetch calendar data:", e);
                }
            } finally {
                if (isMounted) {
                    setCalendarLoading(false);
                }
            }
        };

        const fetchStatsData = async () => {
            try {
                const statsData = await fetchAndProcessData(CURRENT_SEASON.seasonId, CURRENT_SEASON.divisionId);
                if (!isMounted) return;
                const { standings } = statsData;
                setStandings(standings);
                setAllGames(statsData.allGames);
                setFullSchedule(statsData.fullSchedule);
                const jetsData = standings.find(team => team.name.toLowerCase().includes('jets'));
                setJetsTeam(jetsData || null);
            } catch (e: any) {
                if (isMounted) {
                    setStatsError(e.message);
                    console.error("Failed to fetch stats data:", e);
                }
            } finally {
                if (isMounted) {
                    setStatsLoading(false);
                }
            }
        };

        if (isAuthReady) {
            fetchCalendarData();
        }
        fetchStatsData();

        return () => {
            isMounted = false;
        };
    }, [isViewingPastSeason, selectedSeason.key, selectedSeason.label, isAuthReady]);

    const shouldShowBirthdayCard = !statsLoading && !statsError && (isBirthdayToday || (daysUntilBirthday !== null && daysUntilBirthday <= 5));

    const BirthdayCard = shouldShowBirthdayCard ? (
        <Paper variant="outlined" elevation={0} sx={{ borderRadius: 2, mb: 3, overflow: 'hidden' }}>
            <Box sx={{ p: { xs: 2, sm: 3 }, pb: 2 }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <CakeIcon color="action" />
                    <Typography
                        variant="h5"
                        component="h1"
                        sx={{
                            fontSize: { xs: '1.25rem', sm: '1.5rem' },
                            fontWeight: 600,
                            color: 'text.primary',
                        }}
                    >
                        Next Birthday
                    </Typography>
                </Stack>
            </Box>
            <Divider />
            <Box sx={{ p: { xs: 2, sm: 3 } }}>
                {isBirthdayToday && nextBirthdayPlayer ? (
                    <Typography variant="h6" align="center" color="primary.main" sx={{ fontWeight: 'bold' }}>
                        Happy Birthday {nextBirthdayPlayer.nickname || nextBirthdayPlayer.firstName}!!!
                    </Typography>
                ) : nextBirthdayPlayer && daysUntilBirthday !== null ? (
                    <Box>
                        <Typography variant="body1">
                            <strong>{nextBirthdayPlayer.nickname || nextBirthdayPlayer.firstName}'s</strong> birthday is in <strong>{daysUntilBirthday}</strong> {daysUntilBirthday === 1 ? 'day' : 'days'}.
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {new Date(nextBirthdayPlayer.dob).toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}
                        </Typography>
                    </Box>
                ) : null}
            </Box>
            <Divider />
            <Box
                sx={{
                    px: { xs: 2, sm: 3 },
                    py: 1.5,
                    bgcolor: 'grey.50',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 1,
                }}
            >
                <Button
                    component={RouterLink}
                    to="/roster"
                    variant="outlined"
                    size="small"
                    onClick={() => logEvent('click_view_roster_birthday')}
                >
                    View Roster
                </Button>
            </Box>
        </Paper>
    ) : null;

    const isNextEventAGame = nextEvents.length > 0 && nextEvents.every(e => {
        const type = getEventType(e);
        return type === EventType.Game || type === EventType.Exhibition || type === EventType.Tournament;
    });

    const hasSeparateNextGame = !!nextGame;

    const NextEventCard = (
        <Paper variant="outlined" elevation={0} sx={{ borderRadius: 2, mb: 3, overflow: 'hidden' }}>
            <Box sx={{ p: { xs: 2, sm: 3 }, pb: 2 }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                    {isNextEventAGame ? <SportsHockeyIcon color="action" /> : <EventIcon color="action" />}
                    <Typography
                        variant="h5"
                        component="h1"
                        sx={{
                            fontSize: { xs: '1.25rem', sm: '1.5rem' },
                            fontWeight: 600,
                            color: 'text.primary',
                        }}
                    >
                        {isNextEventAGame
                            ? (nextEvents.length > 1 ? "Next Games" : "Next Game")
                            : (nextEvents.length > 1 ? "Next Events" : "Next Event")}
                    </Typography>
                </Stack>
            </Box>
            <Divider />
            <Box sx={{ p: { xs: 2, sm: 3 } }}>
                {calendarError ? (
                    <Alert severity="error">Could not load events: {calendarError}</Alert>
                ) : nextEvents.length > 0 ? (
                    <Stack spacing={2} divider={<Divider sx={{ borderStyle: 'dashed' }} />}>
                        {nextEvents.map((event, index) => (
                            <SingleEventContent key={`${event.summary}-${event.start}-${index}`} event={event} />
                        ))}
                    </Stack>
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        No upcoming events scheduled.
                    </Typography>
                )}
            </Box>
            <Divider />
            <Box
                sx={{
                    px: { xs: 2, sm: 3 },
                    py: 1.5,
                    bgcolor: 'grey.50',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 1,
                }}
            >
                {nextEvents.some(e => e.url && e.isTournament) && (
                    <Button 
                        component={RouterLink}
                        to={nextEvents.find(e => e.url && e.isTournament)?.url!}
                        variant="outlined"
                        size="small"
                        onClick={() => logEvent('click_tournament_details_next_event')}
                    >
                        Tournament Details
                    </Button>
                )}
                <Button 
                    component={RouterLink}
                    to="/calendar"
                    variant="outlined"
                    size="small"
                    onClick={() => logEvent('click_view_all_events')}
                >
                    All Events
                </Button>
            </Box>
        </Paper>
    );

    const NextGameCard = nextGame ? (
        <Paper variant="outlined" elevation={0} sx={{ borderRadius: 2, mb: 3, overflow: 'hidden' }}>
            <Box sx={{ p: { xs: 2, sm: 3 }, pb: 2 }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <SportsHockeyIcon color="action" />
                    <Typography
                        variant="h5"
                        component="h1"
                        sx={{
                            fontSize: { xs: '1.25rem', sm: '1.5rem' },
                            fontWeight: 600,
                            color: 'text.primary',
                        }}
                    >
                        Next Game
                    </Typography>
                </Stack>
            </Box>
            <Divider />
            <Box sx={{ p: { xs: 2, sm: 3 } }}>
                <SingleEventContent event={nextGame} />
            </Box>
            <Divider />
            <Box
                sx={{
                    px: { xs: 2, sm: 3 },
                    py: 1.5,
                    bgcolor: 'grey.50',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 1,
                }}
            >
                {nextGame.url && nextGame.isTournament && (
                    <Button 
                        component={RouterLink}
                        to={nextGame.url}
                        variant="outlined"
                        size="small"
                        onClick={() => logEvent('click_tournament_details_next_game', { url: nextGame.url })}
                    >
                        Tournament Details
                    </Button>
                )}
                <Button 
                    component={RouterLink}
                    to="/calendar"
                    variant="outlined"
                    size="small"
                    onClick={() => logEvent('click_view_all_events')}
                >
                    All Events
                </Button>
            </Box>
        </Paper>
    ) : null;

    const LastWeekMatchupsCard = (
        <WeeklyMatchups
            allGames={allGames}
            user={user}
            fullSchedule={fullSchedule}
            seasonId={CURRENT_SEASON.seasonId}
            divisionId={CURRENT_SEASON.divisionId}
            section="lastWeek"
        />
    );

    const UpcomingMatchupsCard = (
        <WeeklyMatchups
            allGames={allGames}
            user={user}
            fullSchedule={fullSchedule}
            seasonId={CURRENT_SEASON.seasonId}
            divisionId={CURRENT_SEASON.divisionId}
            section="upcoming"
        />
    );

    const CurrentStandingCard = allGames.length > 0 && jetsTeam ? (
        <Paper variant="outlined" elevation={0} sx={{ borderRadius: 2, mb: 3, overflow: 'hidden' }}>
             <Box sx={{ p: { xs: 2, sm: 3 }, pb: 2 }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <LeaderboardIcon color="action" />
                    <Typography
                        variant="h5"
                        component="h1"
                        sx={{
                            fontSize: { xs: '1.25rem', sm: '1.5rem' },
                            fontWeight: 600,
                            color: 'text.primary',
                        }}
                    >
                        Current Standing
                    </Typography>
                </Stack>
            </Box>
            <Divider />
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Rank</TableCell>
                            <TableCell>Team</TableCell>
                            <TableCell align="right">W</TableCell>
                            <TableCell align="right">L</TableCell>
                            <TableCell align="right">T</TableCell>
                            <TableCell align="right">PTS</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <StandingsRow
                            team={jetsTeam}
                            isExpanded={isStandingsExpanded}
                            onRowClick={() => {
                                setIsStandingsExpanded(!isStandingsExpanded);
                                logEvent('expand_jets_stats_home', { expanded: !isStandingsExpanded });
                            }}
                            allGames={allGames}
                            fullSchedule={fullSchedule}
                        />
                    </TableBody>
                </Table>
            </TableContainer>
            <Divider />
            <Box
                sx={{
                    px: { xs: 2, sm: 3 },
                    py: 1.5,
                    bgcolor: 'grey.50',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 1,
                }}
            >
                <Button
                    component={RouterLink}
                    to={`/standings/${CURRENT_SEASON.key}`}
                    variant="outlined"
                    size="small"
                    onClick={() => logEvent('click_view_all_standings')}
                >
                    All Standings
                </Button>
            </Box>
        </Paper>
    ) : null;

    // --- Skeletons for Loading State ---
    const NextEventSkeletonCard = (
        <Paper variant="outlined" elevation={0} sx={{ borderRadius: 2, mb: 3, overflow: 'hidden' }}>
            <Box sx={{ p: { xs: 2, sm: 3 }, pb: 2 }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <EventIcon color="action" />
                    <Typography variant="h5" component="h1" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' }, fontWeight: 600 }}>
                        Next Event
                    </Typography>
                </Stack>
            </Box>
            <Divider />
            <Box sx={{ p: { xs: 2, sm: 3 } }}>
                <Skeleton variant="text" width="80%" height={24} sx={{ mb: 0.5 }} />
                <Skeleton variant="text" width="60%" height={20} sx={{ mb: 0.5 }} />
                <Skeleton variant="text" width="50%" height={20} />
            </Box>
            <Divider />
            <Box sx={{ px: { xs: 2, sm: 3 }, py: 1.5, bgcolor: 'grey.50', display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                <Button variant="outlined" size="small" disabled>
                    All Events
                </Button>
            </Box>
        </Paper>
    );

    const NextGameSkeletonCard = (
        <Paper variant="outlined" elevation={0} sx={{ borderRadius: 2, mb: 3, overflow: 'hidden' }}>
            <Box sx={{ p: { xs: 2, sm: 3 }, pb: 2 }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <SportsHockeyIcon color="action" />
                    <Typography variant="h5" component="h1" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' }, fontWeight: 600 }}>
                        Next Game
                    </Typography>
                </Stack>
            </Box>
            <Divider />
            <Box sx={{ p: { xs: 2, sm: 3 } }}>
                <Skeleton variant="text" width="80%" height={24} sx={{ mb: 0.5 }} />
                <Skeleton variant="text" width="60%" height={20} sx={{ mb: 0.5 }} />
                <Skeleton variant="text" width="50%" height={20} />
            </Box>
            <Divider />
            <Box sx={{ px: { xs: 2, sm: 3 }, py: 1.5, bgcolor: 'grey.50', display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                <Button variant="outlined" size="small" disabled>
                    All Events
                </Button>
            </Box>
        </Paper>
    );

    const TournamentSkeletonCard = (
        <Paper variant="outlined" elevation={0} sx={{ borderRadius: 2, mb: 3, overflow: 'hidden' }}>
            <Box sx={{ p: { xs: 2, sm: 3 }, pb: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1.5}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <EmojiEventsIcon color="action" />
                        <Typography variant="h5" component="h1" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' }, fontWeight: 600 }}>
                            Upcoming Tournament
                        </Typography>
                    </Stack>
                    <Skeleton variant="rounded" width={75} height={24} />
                </Stack>
            </Box>
            <Divider />
            <Box sx={{ p: { xs: 2, sm: 3 } }}>
                <Stack direction="row" spacing={2} alignItems="center">
                    <Skeleton variant="rectangular" width={56} height={44} sx={{ borderRadius: 1 }} />
                    <Box sx={{ flex: 1 }}>
                        <Skeleton variant="text" width="70%" height={24} />
                        <Skeleton variant="text" width="45%" height={20} />
                    </Box>
                </Stack>
                <Skeleton variant="rounded" height={44} sx={{ mt: 2 }} />
            </Box>
            <Divider />
            <Box
                sx={{
                    px: { xs: 2, sm: 3 },
                    py: 1.5,
                    bgcolor: 'grey.50',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 1
                }}
            >
                <Skeleton variant="text" width={180} height={20} />
                <Button variant="outlined" size="small" disabled>
                    View Tournament
                </Button>
            </Box>
        </Paper>
    );

    const CurrentStandingSkeletonCard = (
        <Paper variant="outlined" elevation={0} sx={{ borderRadius: 2, mb: 3, overflow: 'hidden' }}>
            <Box sx={{ p: { xs: 2, sm: 3 }, pb: 2 }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <LeaderboardIcon color="action" />
                    <Typography variant="h5" component="h1" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' }, fontWeight: 600 }}>
                        Current Standing
                    </Typography>
                </Stack>
            </Box>
            <Divider />
            <Box sx={{ p: { xs: 2, sm: 3 } }}>
                <Skeleton variant="text" width="60%" height={32} />
                <Skeleton variant="rectangular" height={48} sx={{ borderRadius: 1, mt: 1 }} />
            </Box>
            <Divider />
            <Box sx={{ px: { xs: 2, sm: 3 }, py: 1.5, bgcolor: 'grey.50', display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                <Button variant="outlined" size="small" disabled>
                    All Standings
                </Button>
            </Box>
        </Paper>
    );

    const WeeklyMatchupsSkeletonCard = (title: string) => (
        <Paper variant="outlined" elevation={0} sx={{ borderRadius: 2, mb: 3, overflow: 'hidden' }}>
            <Box sx={{ p: { xs: 2, sm: 3 }, pb: 2 }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <SportsHockeyIcon color="action" />
                    <Typography variant="h5" component="h1" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' }, fontWeight: 600 }}>
                        {title}
                    </Typography>
                </Stack>
            </Box>
            <Divider />
            <Box sx={{ p: { xs: 2, sm: 3 } }}>
                <Stack spacing={1.5}>
                    <Skeleton variant="text" width="90%" height={24} />
                    <Skeleton variant="text" width="85%" height={24} />
                    <Skeleton variant="text" width="80%" height={24} />
                </Stack>
            </Box>
            <Divider />
            <Box sx={{ px: { xs: 2, sm: 3 }, py: 1.5, bgcolor: 'grey.50', display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                <Button variant="outlined" size="small" disabled>
                    All Standings
                </Button>
            </Box>
        </Paper>
    );

    const DutySkeletonCard = (
        <Paper variant="outlined" elevation={0} sx={{ borderRadius: 2, mb: 3, overflow: 'hidden' }}>
            <Box sx={{ p: { xs: 2, sm: 3 }, pb: 2 }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <AssignmentIndIcon color="action" />
                    <Typography variant="h5" component="h1" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' }, fontWeight: 600 }}>
                        Your Next Duty
                    </Typography>
                </Stack>
            </Box>
            <Divider />
            <Box sx={{ p: { xs: 2, sm: 3 } }}>
                <Skeleton variant="text" width="75%" height={24} sx={{ mb: 0.5 }} />
                <Skeleton variant="text" width="50%" height={20} />
            </Box>
            <Divider />
            <Box sx={{ px: { xs: 2, sm: 3 }, py: 1.5, bgcolor: 'grey.50', display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                <Button variant="outlined" size="small" disabled>
                    View All Duties
                </Button>
            </Box>
        </Paper>
    );

    const BirthdaySkeletonCard = (
        <Paper variant="outlined" elevation={0} sx={{ borderRadius: 2, mb: 3, overflow: 'hidden' }}>
            <Box sx={{ p: { xs: 2, sm: 3 }, pb: 2 }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <CakeIcon color="action" />
                    <Typography variant="h5" component="h1" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' }, fontWeight: 600 }}>
                        Next Birthday
                    </Typography>
                </Stack>
            </Box>
            <Divider />
            <Box sx={{ p: { xs: 2, sm: 3 } }}>
                <Skeleton variant="text" width="60%" height={24} sx={{ mb: 0.5 }} />
                <Skeleton variant="text" width="40%" height={20} />
            </Box>
            <Divider />
            <Box sx={{ px: { xs: 2, sm: 3 }, py: 1.5, bgcolor: 'grey.50', display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                <Button variant="outlined" size="small" disabled>
                    View Roster
                </Button>
            </Box>
        </Paper>
    );

    const ScheduleConflictSkeletonCard = (
        <Paper variant="outlined" elevation={0} sx={{ borderRadius: 2, mb: 3 }}>
            <Box sx={{ p: { xs: 2, sm: 3 }, pb: 2 }}>
                <Skeleton variant="text" width="50%" height={28} />
            </Box>
            <Divider />
            <Box sx={{ p: { xs: 2, sm: 3 } }}>
                <Skeleton variant="rectangular" height={60} sx={{ borderRadius: 1 }} />
            </Box>
        </Paper>
    );

    // --- Compute Live Card Ordering ---
    const { liveDesktopLeft, liveDesktopRight, liveMobile } = useMemo(() => {
        const dLeft: HomeCardId[] = [];
        const dRight: HomeCardId[] = [];
        const mob: HomeCardId[] = [];

        const hasConflicts = isTeamManager && scheduleConflicts.length > 0;
        const tournamentCardId: HomeCardId = isTournamentBeforeNextEvent ? 'tournament_immediate' : 'tournament_upcoming';
        const hasTournament = !!activeOrUpcomingTournament;
        const hasStandings = allGames.length > 0 && !!jetsTeam;
        const dutyCardId: HomeCardId | null = daysUntilNextDuty !== null
            ? (daysUntilNextDuty <= 5 ? 'next_duty_urgent' : 'next_duty_non_urgent')
            : null;

        // --- Mobile Layout (Single Column Priority) ---
        if (hasConflicts) {
            mob.push('schedule_conflicts');
        }
        if (dutyCardId === 'next_duty_urgent') {
            mob.push('next_duty_urgent');
        }
        if (shouldShowBirthdayCard) {
            mob.push('birthday');
        }
        if (hasTournament && isTournamentBeforeNextEvent) {
            mob.push('tournament_immediate');
        }
        mob.push('next_event');
        if (hasSeparateNextGame) {
            mob.push('next_game');
        }
        if (hasTournament && !isTournamentBeforeNextEvent) {
            mob.push('tournament_upcoming');
        }
        if (hasStandings) {
            mob.push('current_standing');
        }
        mob.push('last_week_matchups');
        mob.push('upcoming_matchups');
        if (dutyCardId === 'next_duty_non_urgent') {
            mob.push('next_duty_non_urgent');
        }

        // --- Desktop Layout (2-Column Balanced) ---
        const pushDesktopBalanced = (cardId: HomeCardId) => {
            if (dLeft.length <= dRight.length) {
                dLeft.push(cardId);
            } else {
                dRight.push(cardId);
            }
        };

        // 1. Top Row
        if (hasConflicts) {
            dLeft.push('schedule_conflicts');
            dRight.push('next_event');
            if (hasSeparateNextGame) {
                dLeft.push('next_game');
            }
            if (hasTournament) {
                dRight.push(tournamentCardId);
            }
        } else {
            dLeft.push('next_event');
            if (hasSeparateNextGame) {
                dRight.push('next_game');
                if (hasTournament) {
                    pushDesktopBalanced(tournamentCardId);
                }
            } else if (hasTournament) {
                dRight.push(tournamentCardId);
            } else if (hasStandings) {
                dRight.push('current_standing');
            }
        }

        // 2. Secondary Contextual Cards (balanced dynamically)
        if (shouldShowBirthdayCard) {
            pushDesktopBalanced('birthday');
        }
        if (dutyCardId) {
            pushDesktopBalanced(dutyCardId);
        }
        if (hasStandings && !dLeft.includes('current_standing') && !dRight.includes('current_standing')) {
            pushDesktopBalanced('current_standing');
        }

        // 3. Bottom Matchups (always left and right)
        dLeft.push('last_week_matchups');
        dRight.push('upcoming_matchups');

        return {
            liveDesktopLeft: dLeft,
            liveDesktopRight: dRight,
            liveMobile: mob
        };
    }, [
        isTeamManager,
        scheduleConflicts.length,
        daysUntilNextDuty,
        shouldShowBirthdayCard,
        activeOrUpcomingTournament,
        isTournamentBeforeNextEvent,
        hasSeparateNextGame,
        allGames.length,
        jetsTeam
    ]);

    // Persist live layout to localStorage once loading completes
    useEffect(() => {
        if (!isLoading && !isViewingPastSeason) {
            setCachedHomeLayout({
                desktopLeft: liveDesktopLeft,
                desktopRight: liveDesktopRight,
                mobile: liveMobile,
                seasonKey: selectedSeason.key,
                timestamp: Date.now()
            });
        }
    }, [isLoading, isViewingPastSeason, liveDesktopLeft, liveDesktopRight, liveMobile, selectedSeason.key]);

    const renderCardSkeleton = (cardId: HomeCardId) => {
        switch (cardId) {
            case 'schedule_conflicts':
                return ScheduleConflictSkeletonCard;
            case 'next_duty_urgent':
            case 'next_duty_non_urgent':
                return DutySkeletonCard;
            case 'birthday':
                return BirthdaySkeletonCard;
            case 'tournament_immediate':
            case 'tournament_upcoming':
                return TournamentSkeletonCard;
            case 'next_event':
                return NextEventSkeletonCard;
            case 'next_game':
                return NextGameSkeletonCard;
            case 'current_standing':
                return CurrentStandingSkeletonCard;
            case 'last_week_matchups':
                return WeeklyMatchupsSkeletonCard('Last Week Matchups');
            case 'upcoming_matchups':
                return WeeklyMatchupsSkeletonCard('Upcoming League Matchups');
            default:
                return null;
        }
    };

    const renderCardLive = (cardId: HomeCardId) => {
        switch (cardId) {
            case 'schedule_conflicts':
                return ScheduleConflictCardElement;
            case 'next_duty_urgent':
            case 'next_duty_non_urgent':
                return NextDutyCardElement;
            case 'birthday':
                return BirthdayCard;
            case 'tournament_immediate':
            case 'tournament_upcoming':
                return tournamentCardElement;
            case 'next_event':
                return NextEventCard;
            case 'next_game':
                return NextGameCard;
            case 'current_standing':
                return CurrentStandingCard;
            case 'last_week_matchups':
                return LastWeekMatchupsCard;
            case 'upcoming_matchups':
                return UpcomingMatchupsCard;
            default:
                return null;
        }
    };

    if (isViewingPastSeason) {
        return (
            <Container maxWidth="sm">
                <Box sx={{ my: 6 }}>
                    <Paper variant="outlined" elevation={0} sx={{ p: { xs: 3, sm: 4 }, borderRadius: 2, textAlign: 'center' }}>
                        <img 
                            src={process.env.PUBLIC_URL + '/csdhl_logo.png'} 
                            alt="CSDHL Logo" 
                            style={{ height: 64, width: 'auto', marginBottom: 16 }} 
                        />
                        <Typography variant="h5" component="h1" gutterBottom fontWeight="bold">
                            You are viewing the Jets 2014 {selectedSeason.label} - {selectedSeason.divisionName}
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                            Historical season archives are available for review. Calendar events and active schedules are not active for past seasons.
                        </Typography>
                        <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap" useFlexGap sx={{ gap: 1 }}>
                            <Button 
                                variant="contained" 
                                component={RouterLink} 
                                to={`/standings/${selectedSeason.key}`}
                                startIcon={<LeaderboardIcon />}
                                size="large"
                            >
                                View Standings
                            </Button>
                            {selectedSeason.key === 'pw_25_26' && (
                                <>
                                    <Button 
                                        variant="outlined" 
                                        component={RouterLink} 
                                        to="/roster"
                                        startIcon={<ContactsIcon />}
                                    >
                                        View Roster
                                    </Button>
                                    <Button 
                                        variant="outlined" 
                                        component={RouterLink} 
                                        to="/links"
                                        startIcon={<LinkIcon />}
                                    >
                                        View Links
                                    </Button>
                                </>
                            )}
                        </Stack>
                    </Paper>
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth={isDesktop ? "lg" : "sm"}>
            <Box sx={{ my: 4 }}>
                {isDesktop ? (
                    <Grid container spacing={3} alignItems="flex-start">
                        {/* Left Column */}
                        <Grid size={{ xs: 12, md: 6 }}>
                            {isLoading
                                ? cachedLayout.desktopLeft.map(cardId => (
                                    <Fragment key={`skeleton-left-${cardId}`}>{renderCardSkeleton(cardId)}</Fragment>
                                ))
                                : liveDesktopLeft.map(cardId => (
                                    <Fragment key={`live-left-${cardId}`}>{renderCardLive(cardId)}</Fragment>
                                ))
                            }
                        </Grid>

                        {/* Right Column */}
                        <Grid size={{ xs: 12, md: 6 }}>
                            {isLoading
                                ? cachedLayout.desktopRight.map(cardId => (
                                    <Fragment key={`skeleton-right-${cardId}`}>{renderCardSkeleton(cardId)}</Fragment>
                                ))
                                : liveDesktopRight.map(cardId => (
                                    <Fragment key={`live-right-${cardId}`}>{renderCardLive(cardId)}</Fragment>
                                ))
                            }
                        </Grid>
                    </Grid>
                ) : (
                    <Box>
                        {isLoading
                            ? cachedLayout.mobile.map(cardId => (
                                <Fragment key={`skeleton-mobile-${cardId}`}>{renderCardSkeleton(cardId)}</Fragment>
                            ))
                            : liveMobile.map(cardId => (
                                <Fragment key={`live-mobile-${cardId}`}>{renderCardLive(cardId)}</Fragment>
                            ))
                        }
                    </Box>
                )}
            </Box>
        </Container>
    );
};

export default HomePage;

